// ==========================================
// FILE: \src\screens\MapScreen.js
// ==========================================

import React, { useState, useEffect, useMemo } from 'react';
import { 
  View, Text, StyleSheet, TouchableOpacity, ScrollView, 
  ActivityIndicator, Platform, Modal, Image
} from 'react-native';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { useWineContext } from '../context/WineContext'; 
import { callGemini } from '../utils/geminiClient'; 
import EnergyHeader from '../components/EnergyHeader'; 
import { 
  ArrowLeft, Sparkles, AlertCircle, X, Compass, MapPin, 
  Info, RefreshCw, CheckCircle2, ChevronRight
} from 'lucide-react-native';

import OutOfEnergyModal from '../components/OutOfEnergyModal';
import RewardedAdModal from '../components/RewardedAdModal';

const isWeb = Platform.OS === 'web';

// ==========================================
// IMPORTAZIONI CONDIZIONALI MAPPE
// ==========================================
let MapContainer, TileLayer, MarkerWeb, PopupWeb, L, MarkerClusterGroup;
let WebView;

if (isWeb) {
  if (typeof document !== 'undefined') {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
  }
  const ReactLeaflet = require('react-leaflet');
  MapContainer = ReactLeaflet.MapContainer;
  TileLayer = ReactLeaflet.TileLayer;
  MarkerWeb = ReactLeaflet.Marker;
  PopupWeb = ReactLeaflet.Popup;
  MarkerClusterGroup = require('react-leaflet-cluster').default;
  L = require('leaflet');
} else {
  WebView = require('react-native-webview').WebView;
}

const stringHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash);
};

const getCoordinatesFallback = (regionName) => {
  if (!regionName) return { latitude: 41.9, longitude: 12.5 }; 
  const r = regionName.toLowerCase();
  if (r.includes('piemonte') || r.includes('langhe')) return { latitude: 44.6, longitude: 8.0 };
  if (r.includes('tosca') || r.includes('chianti') || r.includes('bolgheri')) return { latitude: 43.4, longitude: 11.3 };
  if (r.includes('venet') || r.includes('valpolicella')) return { latitude: 45.4, longitude: 11.8 };
  if (r.includes('sicilia') || r.includes('etna')) return { latitude: 37.6, longitude: 14.0 };
  if (r.includes('puglia')) return { latitude: 40.7, longitude: 17.0 };
  if (r.includes('emilia') || r.includes('romagna')) return { latitude: 44.5, longitude: 11.0 };
  if (r.includes('bordeaux')) return { latitude: 44.8, longitude: -0.5 };
  if (r.includes('champagne')) return { latitude: 49.0, longitude: 4.0 };
  if (r.includes('bourgogne')) return { latitude: 47.0, longitude: 4.8 };
  const hash = stringHash(r);
  const offsetLat = ((hash % 100) / 100) * 1.5 - 0.75;
  const offsetLng = (((hash / 100) % 100) / 100) * 1.5 - 0.75;
  return { latitude: 42.0 + offsetLat, longitude: 12.0 + offsetLng };
};

export default function MapScreen({ navigation }) {
  const { presentWines, updateWineLocal, consumeEnergy, addEnergy, userProfile, t } = useWineContext();
  const isPremium = userProfile?.isPremium || false;
  const user = auth.currentUser;

  const [groupedRegions, setGroupedRegions] = useState([]);
  const [terroirMeta, setTerroirMeta] = useState({}); 
  const [unmappedWines, setUnmappedWines] = useState([]);
  
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [terroirData, setTerroirData] = useState(null);
  const [terroirLang, setTerroirLang] = useState(null); // MARCATORE LINGUA DEL TERROIR CARICATO
  const [isLoadingTerroir, setIsLoadingTerroir] = useState(false);
  
  const [isGeneratingTerroir, setIsGeneratingTerroir] = useState(false);
  const [isGeolocating, setIsGeolocating] = useState(false);

  const [customAlert, setCustomAlert] = useState({ visible: false, title: '', message: '', type: 'info' });

  const [showOutOfEnergy, setShowOutOfEnergy] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);

  const showAppAlert = (title, message, type = 'info') => setCustomAlert({ visible: true, title, message, type });
  const closeAppAlert = () => setCustomAlert({ visible: false, title: '', message: '', type: 'info' });

  const handleAdComplete = () => {
    setShowAdModal(false);
    addEnergy(5);
    showAppAlert(t('map.alerts.adCompleteTitle'), t('map.alerts.adCompleteMsg'), "success");
  };

  // Verifica se l'Audit Terroir caricato necessita di essere riallineato alla lingua della UI
  const terroirNeedsTranslation = useMemo(() => {
    if (!terroirData) return false;
    const currentAppLang = userProfile?.language || 'it';
    const currentDataLang = terroirLang || 'it';
    return currentDataLang !== currentAppLang;
  }, [terroirData, terroirLang, userProfile?.language]);

  useEffect(() => {
    const fetchTerroirMeta = async () => {
      if (!user) return;
      try {
        const q = collection(db, 'users', user.uid, 'terroirs');
        const snap = await getDocs(q);
        const meta = {};
        snap.forEach(doc => { meta[doc.id] = doc.data().lastBottleCount || 0; });
        setTerroirMeta(meta);
      } catch (error) { console.log(error); }
    };
    fetchTerroirMeta();
  }, [user]);

  useEffect(() => {
    const groups = {};
    const toMap = [];
    
    presentWines.forEach(wine => {
      const hasPreciseCoords = !!wine.preciseZone && !!wine.coords;
      if (!hasPreciseCoords) toMap.push(wine);

      const regionName = hasPreciseCoords ? wine.preciseZone : (wine.technicalSheet?.regione?.trim() || 'Sconosciuta');
      const safeRegion = (regionName.toLowerCase() === 'sconosciuta' || regionName === '') ? t('map.sheet.notSpecified') : regionName;
      const safeId = safeRegion.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      
      if (!groups[safeId]) {
        groups[safeId] = { 
          id: safeId, name: safeRegion, wines: [], count: 0, 
          coords: hasPreciseCoords ? wine.coords : getCoordinatesFallback(safeRegion) 
        };
      }
      groups[safeId].wines.push(wine);
      groups[safeId].count += wine.quantity;
    });

    const regionsArray = Object.values(groups).map(group => {
      const savedCount = terroirMeta[group.id] !== undefined ? terroirMeta[group.id] : -1;
      group.needsUpdate = group.name !== t('map.sheet.notSpecified') && group.count > savedCount;
      return group;
    }).sort((a, b) => b.count - a.count);

    setGroupedRegions(regionsArray);
    setUnmappedWines(toMap);
  }, [presentWines, terroirMeta]);

  useEffect(() => {
    const fetchTerroir = async () => {
      if (!user || !selectedRegion || selectedRegion.name === t('map.sheet.notSpecified')) return;
      setIsLoadingTerroir(true);
      setTerroirData(null);
      setTerroirLang(null);
      try {
        const docRef = doc(db, 'users', user.uid, 'terroirs', selectedRegion.id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setTerroirData(data.story);
          setTerroirLang(data.generatedLanguage || 'it'); // Fallback storico su 'it'
        }
      } catch (error) { console.log(error); } 
      finally { setIsLoadingTerroir(false); }
    };
    if (selectedRegion) fetchTerroir();
  }, [selectedRegion, user]);

  const generateTerroirStory = async () => {
    if (isGeneratingTerroir) return;
    if (!user || !selectedRegion) return;

    setIsGeneratingTerroir(true);

    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      setIsGeneratingTerroir(false); 
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      setIsGeneratingTerroir(false); 
      return;
    }

    try {
      const langNames = { it: "Italiano", en: "Inglese (English)", fr: "Francese (Français)" };
      const targetLanguageName = langNames[userProfile?.language || 'it'] || "Italiano";

      const wineNames = selectedRegion.wines.map(w => w.name).join(', ');
      const promptText = `Sei un Master Sommelier. L'utente possiede questi vini dalla zona "${selectedRegion.name}": ${wineNames}. 
      Scrivi un report testuale (2 paragrafi) valutando il terroir di questa area (clima, geologia, altitudine) e spiegando come questi fattori influenzano i vini dell'utente. Rispondi rigorosamente in lingua ${targetLanguageName}.`;
      
      const story = await callGemini(promptText, false);
      
      const newTerroirMeta = { 
        story, 
        lastBottleCount: selectedRegion.count, 
        updatedAt: Date.now(),
        generatedLanguage: userProfile?.language || 'it' // SALVIAMO LA NUOVA LINGUA CORRENTE
      };
      await setDoc(doc(db, 'users', user.uid, 'terroirs', selectedRegion.id), newTerroirMeta);
      
      setTerroirData(story);
      setTerroirLang(userProfile?.language || 'it');
      setTerroirMeta(prev => ({...prev, [selectedRegion.id]: selectedRegion.count}));
      setSelectedRegion(prev => ({ ...prev, needsUpdate: false })); 

    } catch (error) { 
      if (!isPremium) await addEnergy(1);
      showAppAlert(t('map.alerts.terroirErrorTitle'), t('map.alerts.terroirErrorMsg'), "error"); 
    } finally { 
      setIsGeneratingTerroir(false); 
    }
  };

  const geolocatePreciseWines = async () => {
    if (isGeolocating) return;
    if (unmappedWines.length === 0) return;

    setIsGeolocating(true);

    if (!isPremium && userProfile?.energia < 1) {
      setShowOutOfEnergy(true);
      setIsGeolocating(false); 
      return;
    }

    const hasEnergy = await consumeEnergy(1);
    if (!hasEnergy) {
      setShowOutOfEnergy(true);
      setIsGeolocating(false); 
      return;
    }
    
    const batch = unmappedWines.slice(0, 15);
    
    try {
      const promptWines = batch.map(w => ({ id: w.id, name: w.name, region: w.technicalSheet?.regione || 'Sconosciuta' }));
      
      const promptText = `Agisci come un geografo esperto di viticoltura. Per ogni vino fornito, identifica la sua micro-zona geografica specifica (es. comune o area DOC/DOCG) e restituisci le coordinate GPS precise del centro di quell'area. 
      Vini: ${JSON.stringify(promptWines)}. 
      Rispondi SOLO con questo JSON esatto: {"locations": [{"id": "id_vino", "zone": "Nome Zona", "lat": 44.1, "lng": 8.1}]}`;
      
      const extractedData = await callGemini(promptText, true);
      const locations = Array.isArray(extractedData?.locations) ? extractedData.locations : [];
      
      for (const item of locations) {
        if (item.id && item.lat && item.lng && item.zone) {
          await updateWineLocal(item.id, { 
            preciseZone: item.zone, 
            coords: { latitude: parseFloat(item.lat), longitude: parseFloat(item.lng) } 
          });
        }
      }
      
      if (locations.length > 0) {
        showAppAlert(t('map.alerts.locateSuccessTitle'), t('map.alerts.locateSuccessMsg'), "success");
      } else {
        throw new Error("Dati vuoti");
      }

    } catch (error) { 
      if (!isPremium) await addEnergy(1);
      showAppAlert(t('map.alerts.geoErrorTitle'), t('map.alerts.geoErrorMsg'), "error"); 
    } finally { 
      setIsGeolocating(false); 
    }
  };

  const generateLeafletHtml = () => {
    const safeData = encodeURIComponent(JSON.stringify(groupedRegions));
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
        <link rel="stylesheet" href="https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.css" />
        <link rel="stylesheet" href="https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.Default.css" />
        <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
        <script src="https://unpkg.com/leaflet.markercluster@1.4.1/dist/leaflet.markercluster.js"></script>
        <style>
          html, body { margin: 0; padding: 0; height: 100%; width: 100%; background-color: #f8fafc; overflow: hidden; }
          #map { position: absolute; top: 0; bottom: 0; left: 0; right: 0; width: 100%; height: 100%; }
          .leaflet-control-attribution { display: none; }
          .custom-leaflet-icon { background: transparent; border: none; }
        </style>
      </head>
      <body>
        <div id="map"></div>
        <script>
          document.addEventListener('DOMContentLoaded', function() {
            try {
              var bounds = [[-90, -180], [90, 180]];
              var map = L.map('map', { zoomControl: false, maxBounds: bounds, maxBoundsViscosity: 1.0 }).setView([43.0, 12.0], 5);
              
              L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                attribution: 'CARTO', maxZoom: 18, noWrap: true, bounds: bounds
              }).addTo(map);

              setTimeout(function(){ map.invalidateSize(); }, 500);

              var rawData = "${safeData}";
              var regionsData = JSON.parse(decodeURIComponent(rawData));

              var markers = L.markerClusterGroup({
                showCoverageOnHover: false,
                iconCreateFunction: function(cluster) {
                  var childMarkers = cluster.getAllChildMarkers();
                  var totalBottles = 0;
                  var needsUpdate = false;
                  
                  childMarkers.forEach(function(m) {
                    totalBottles += m.options.bottleCount;
                    if(m.options.needsUpdate) needsUpdate = true;
                  });

                  var scale = Math.min(3.0, 1 + (totalBottles * 0.03));
                  var color = needsUpdate ? '#ea580c' : '#9f1239';
                  var highlightHtml = needsUpdate ? '<div style="position: absolute; top: -5px; right: -5px; width: 12px; height: 12px; background-color: #ef4444; border-radius: 6px; border: 2px solid white; z-index: 10;"></div>' : '';

                  var html = '<div style="transform: scale(' + scale + '); transform-origin: bottom center; position: relative; display: flex; flex-direction: column; align-items: center;">' +
                    highlightHtml +
                    '<div style="background-color: white; border: 2.5px solid ' + color + '; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; box-shadow: 0px 4px 10px rgba(0,0,0,0.3);">' +
                      '<span style="font-size: 18px; color: ' + color + ';">🍷</span>' +
                    '</div>' +
                    '<div style="position: absolute; bottom: -8px; background: #0f172a; color: white; font-size: 10px; padding: 2px 6px; border-radius: 10px; font-weight: bold; border: 1px solid white;">' + totalBottles + '</div>' +
                  '</div>';

                  return L.divIcon({ className: 'custom-leaflet-icon', html: html, iconSize: [36*scale, 36*scale], iconAnchor: [18*scale, 36*scale] });
                }
              });

              regionsData.forEach(function(region) {
                var scale = Math.min(2.5, 1 + (region.count * 0.05));
                var color = region.needsUpdate ? '#ea580c' : '#7f1d1d';
                var highlightHtml = region.needsUpdate ? '<div style="position: absolute; top: -5px; right: -5px; width: 10px; height: 10px; background-color: #ef4444; border-radius: 5px; border: 1.5px solid white; z-index: 10;"></div>' : '';

                var iconHtml = '<div style="transform: scale(' + scale + '); transform-origin: bottom center; position: relative; display: flex; flex-direction: column; align-items: center;">' +
                  highlightHtml +
                  '<div style="background-color: white; border: 2px solid ' + color + '; border-radius: 50%; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; box-shadow: 0px 4px 10px rgba(0,0,0,0.3);">' +
                    '<span style="font-size: 18px; color: ' + color + ';">🍷</span>' +
                  '</div>' +
                  '<div style="position: absolute; bottom: -8px; background: #0f172a; color: white; font-size: 10px; padding: 2px 6px; border-radius: 10px; font-weight: bold; border: 1px solid white;">' + region.count + '</div>' +
                '</div>';

                var icon = L.divIcon({ className: 'custom-leaflet-icon', html: iconHtml, iconSize: [34*scale, 34*scale], iconAnchor: [17*scale, 34*scale] });

                var marker = L.marker([region.coords.latitude, region.coords.longitude], { 
                  icon: icon, 
                  bottleCount: region.count, 
                  needsUpdate: region.needsUpdate 
                });
                
                marker.on('click', function() {
                  if (window.ReactNativeWebView) {
                    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'MARKER_CLICK', regionId: region.id }));
                  }
                });
                markers.addLayer(marker);
              });
              
              map.addLayer(markers);

            } catch(e) { console.error("Errore Mappa", e); }
          });
        </script>
      </body>
      </html>
    `;
  };

  const handleWebViewMessage = (event) => {
    try {
      const data = JSON.parse(event.nativeEvent.data);
      if (data.type === 'MARKER_CLICK') {
        const selected = groupedRegions.find(r => r.id === data.regionId);
        if (selected) setSelectedRegion(selected);
      }
    } catch (e) { console.log(e); }
  };

  const createClusterCustomIcon = (cluster) => {
    const childMarkers = cluster.getAllChildMarkers();
    let totalBottles = 0;
    let needsUpdate = false;
    childMarkers.forEach(m => {
      totalBottles += m.options.bottleCount;
      if (m.options.needsUpdate) needsUpdate = true;
    });

    const scale = Math.min(3.0, 1 + (totalBottles * 0.03));
    const color = needsUpdate ? '#ea580c' : '#9f1239';
    const highlightHtml = needsUpdate ? `<div style="position: absolute; top: -5px; right: -5px; width: 12px; height: 12px; background-color: #ef4444; border-radius: 6px; border: 2px solid white; z-index: 10;"></div>` : '';

    return new L.DivIcon({
      className: 'custom-leaflet-icon',
      html: `
        <div style="transform: scale(${scale}); transform-origin: bottom center; position: relative; display: flex; flex-direction: column; align-items: center;">
          ${highlightHtml}
          <div style="background-color: white; border: 3px solid ${color}; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; box-shadow: 0px 4px 12px rgba(0,0,0,0.3);">
            <span style="font-size: 20px; color: ${color};">🍷</span>
          </div>
          <div style="position: absolute; bottom: -8px; background: #0f172a; color: white; font-size: 11px; padding: 2px 6px; border-radius: 10px; font-weight: bold; border: 1.5px solid white;">${totalBottles}</div>
        </div>
      `,
      iconSize: [40 * scale, 40 * scale], iconAnchor: [20 * scale, 40 * scale]
    });
  };

  const getLeafletIconWeb = (region) => {
    if (!isWeb) return null;
    const scale = Math.min(2.5, 1 + (region.count * 0.05));
    const color = region.needsUpdate ? '#ea580c' : '#7f1d1d';
    return new L.DivIcon({
      className: 'custom-leaflet-icon',
      html: `
        <div style="transform: scale(${scale}); transform-origin: bottom center; position: relative; display: flex; flex-direction: column; align-items: center;">
          ${region.needsUpdate ? `<div style="position: absolute; top: -5px; right: -5px; width: 10px; height: 10px; background-color: #ef4444; border-radius: 5px; border: 1.5px solid white; z-index: 10;"></div>` : ''}
          <div style="background-color: white; border: 2px solid ${color}; border-radius: 50%; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; box-shadow: 0px 4px 10px rgba(0,0,0,0.3);">
            <span style="font-size: 18px; color: ${color};">🍷</span>
          </div>
          <div style="position: absolute; bottom: -8px; background: #0f172a; color: white; font-size: 10px; padding: 2px 6px; border-radius: 10px; font-weight: bold; border: 1px solid white;">${region.count}</div>
        </div>
      `,
      iconSize: [34 * scale, 34 * scale], iconAnchor: [17 * scale, 34 * scale]
    });
  };

  return (
    <View style={styles.mainContainer}>
      
      <View style={styles.topRedBanner}>
         <EnergyHeader />
      </View>

      <View style={styles.pageHeaderCushion}>
        <View style={styles.headerLeft}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <ArrowLeft color="#0f172a" size={24} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('map.title')}</Text>
        </View>
      </View>

      <View style={styles.cardsRow}>
        
        <View style={styles.dashboardCard}>
          <View style={styles.cardIconWrap}><Compass color="#0f172a" size={22} /></View>
          <View>
            <Text style={styles.cardTitle}>{t('map.dashboard.terroirTitle')}</Text>
            <Text style={styles.cardSub}>{t('map.dashboard.terroirSub')}</Text>
          </View>
        </View>

        {unmappedWines.length > 0 ? (
          <TouchableOpacity style={[styles.dashboardCard, styles.aiCard]} onPress={geolocatePreciseWines} disabled={isGeolocating}>
            <View style={styles.aiCardIconWrap}>
              {isGeolocating ? <ActivityIndicator color="#4f46e5" size="small" /> : <Sparkles color="#4f46e5" size={22} />}
            </View>
            <View>
              <Text style={styles.aiCardTitle}>{t('map.dashboard.findVinesTitle')}</Text>
              <Text style={styles.aiCardSub}>
                {t('map.dashboard.findVinesSub').replace('{count}', Math.min(unmappedWines.length, 15))}
              </Text>
            </View>
          </TouchableOpacity>
        ) : (
          <View style={[styles.dashboardCard, styles.successCard]}>
            <View style={styles.successCardIconWrap}><CheckCircle2 color="#166534" size={22} /></View>
            <View>
              <Text style={styles.successCardTitle}>{t('map.dashboard.cellarMappedTitle')}</Text>
              <Text style={styles.successCardSub}>{t('map.dashboard.cellarMappedSub')}</Text>
            </View>
          </View>
        )}

      </View>

      <View style={styles.mapWrapper}>
        {isWeb ? (
          <MapContainer center={[43.0, 12.0]} zoom={5} style={{ height: '100%', width: '100%' }} zoomControl={false} maxBounds={[[-90, -180], [90, 180]]}>
            <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" noWrap={true} />
            <MarkerClusterGroup showCoverageOnHover={false} iconCreateFunction={createClusterCustomIcon}>
              {groupedRegions.map((region, idx) => (
                <MarkerWeb key={idx} position={[region.coords.latitude, region.coords.longitude]} icon={getLeafletIconWeb(region)} bottleCount={region.count} needsUpdate={region.needsUpdate} eventHandlers={{ click: () => setSelectedRegion(region) }}>
                  <PopupWeb><div style={{textAlign: 'center', fontWeight: 'bold'}}>{region.name}</div></PopupWeb>
                </MarkerWeb>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        ) : (
          <WebView
            originWhitelist={['*']}
            source={{ html: generateLeafletHtml() }}
            style={{ flex: 1, backgroundColor: 'transparent' }}
            onMessage={handleWebViewMessage}
            javaScriptEnabled={true}
            domStorageEnabled={true}
            mixedContentMode="always"
            allowFileAccessFromFileURLs={true}
            allowUniversalAccessFromFileURLs={true}
            scrollEnabled={false}
            bounces={false}
            startInLoadingState={true}
            renderLoading={() => <ActivityIndicator size="large" color="#e11d48" style={{position: 'absolute', top: '50%', left: '50%', marginLeft: -18, marginTop: -18}}/>}
          />
        )}
      </View>

      <View style={styles.modalViewportPlacement}>
        <Modal transparent={true} visible={!!selectedRegion} animationType="slide" onRequestClose={() => setSelectedRegion(null)}>
          <View style={styles.modalOverlay}>
            <View style={styles.bottomSheet}>
              <View style={styles.sheetHeader}>
                <View style={{flex: 1, paddingRight: 10}}><Text style={styles.sheetSubtitle}>{t('map.sheet.territorialAnalysis')}</Text><Text style={styles.sheetTitle} numberOfLines={2}>{selectedRegion?.name}</Text></View>
                <TouchableOpacity style={styles.closeSheetBtn} onPress={() => setSelectedRegion(null)}><X color="#0f172a" size={24} /></TouchableOpacity>
              </View>

              <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{paddingBottom: 40}}>
                {selectedRegion?.name !== t('map.sheet.unspecifiedRegion') && (
                  <View style={styles.reportBox}>
                    <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8}}>
                      <View style={{flexDirection: 'row', alignItems: 'center'}}>
                        <Compass color="#0f172a" size={20} style={{marginRight: 8}} />
                        <Text style={styles.reportTitle}>{t('map.sheet.expertReport')}</Text>
                      </View>
                    </View>
                    
                    {isLoadingTerroir ? (
                      <ActivityIndicator color="#e11d48" style={{marginVertical: 20}} />
                    ) : terroirData ? (
                      <>
                        {/* MINI-BANNER CONDIVISO DI RIALLINEAMENTO LINGUA IA PER AUDIT TERROIR */}
                        {terroirNeedsTranslation && (
                          <View style={styles.translateDataBannerMini}>
                            <Text style={styles.translateDescMini}>{t('details.translateBanner.desc')}</Text>
                            <TouchableOpacity style={styles.translateBtnMini} onPress={generateTerroirStory} disabled={isGeneratingTerroir}>
                              {isGeneratingTerroir ? (
                                <ActivityIndicator color="#fff" size="small" />
                              ) : (
                                <Text style={styles.translateBtnTextMini}>{t('details.translateBanner.action')}</Text>
                              )}
                            </TouchableOpacity>
                          </View>
                        )}
                        
                        <Text style={styles.reportText}>{terroirData}</Text>
                        
                        {selectedRegion?.needsUpdate && !terroirNeedsTranslation && (
                          <TouchableOpacity style={styles.recalcBtn} onPress={generateTerroirStory} disabled={isGeneratingTerroir}>
                            {isGeneratingTerroir ? <ActivityIndicator color="#fff" /> : <Text style={styles.recalcBtnText}>{t('map.sheet.recalcAudit')}</Text>}
                          </TouchableOpacity>
                        )}
                      </>
                    ) : (
                      <View style={{alignItems: 'center', paddingVertical: 10}}>
                        <Text style={styles.emptyAuditText}>{t('map.sheet.firstAuditPrompt')}</Text>
                        <TouchableOpacity style={styles.recalcBtn} onPress={generateTerroirStory} disabled={isGeneratingTerroir}>
                          {isGeneratingTerroir ? <ActivityIndicator color="#fff" /> : <Text style={styles.recalcBtnText}>{t('map.sheet.generateAudit')}</Text>}
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>
                )}

                <Text style={styles.wineListTitle}>
                  {t('map.sheet.myWinesHere').replace('{count}', selectedRegion?.count || 0)}
                </Text>
                {selectedRegion?.wines.map((wine, idx) => (
                  <TouchableOpacity key={idx} style={styles.wineListItem} onPress={() => { setSelectedRegion(null); navigation.navigate('Detail', { wine }); }}>
                    {wine.image ? <Image source={{uri: wine.image}} style={styles.wineThumb} /> : <View style={styles.wineThumbPlaceholder}><Text>🍷</Text></View>}
                    <View style={{flex: 1}}><Text style={styles.wineItemName} numberOfLines={1}>{wine.name}</Text><Text style={styles.wineItemType}>{wine.type} • {wine.annata}</Text></View>
                    <View style={styles.wineItemQty}><Text style={styles.wineItemQtyText}>x{wine.quantity}</Text></View>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>
        </Modal>
      </View>

      <Modal transparent={true} visible={customAlert.visible} animationType="fade">
        <View style={styles.systemModalOverlay}>
          <View style={styles.systemModalBox}>
            <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 16}}>
              {customAlert.type === 'error' ? <AlertCircle color="#ef4444" size={28} /> : <CheckCircle2 color="#10b981" size={28} />}
              <Text style={styles.systemModalTitle}>{customAlert.title}</Text>
            </View>
            <Text style={styles.systemModalMessage}>{customAlert.message}</Text>
            <TouchableOpacity style={styles.systemModalConfirmBtn} onPress={closeAppAlert}><Text style={styles.systemModalConfirmText}>{t('global.ok')}</Text></TouchableOpacity>
          </View>
        </View>
      </Modal>

      <OutOfEnergyModal 
        visible={showOutOfEnergy}
        onClose={() => setShowOutOfEnergy(false)}
        onWatchAd={() => {
          setShowOutOfEnergy(false);
          setShowAdModal(true); 
        }}
        onSubscribe={() => {
          setShowOutOfEnergy(false);
          navigation.navigate('Paywall');
        }}
      />

      <RewardedAdModal 
        visible={showAdModal}
        onClose={() => setShowAdModal(false)}
        onReward={handleAdComplete}
      />

    </View>
  );
}

const styles = StyleSheet.create({
  mainContainer: { flex: 1, backgroundColor: '#f8fafc' },
  topRedBanner: {
    backgroundColor: '#e11d48',
    paddingBottom: 16,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    elevation: 5,
    boxShadow: '0px 4px 6px rgba(225, 29, 72, 0.3)',
    zIndex: 50,
  },
  pageHeaderCushion: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginTop: 16, 
    marginBottom: 20, 
    padding: 16,
    borderRadius: 20,
    elevation: 3,
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.05)',
    zIndex: 10,
  },
  headerLeft: { flexDirection: 'row', alignItems: 'center' },
  backButton: { marginRight: 10, padding: 6, backgroundColor: '#f1f5f9', borderRadius: 10 },
  headerTitle: { fontSize: 22, fontWeight: 'bold', color: '#0f172a' },
  cardsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, paddingHorizontal: 16, marginBottom: 16 },
  dashboardCard: { 
    flex: 1, 
    minWidth: 140,
    backgroundColor: 'white', 
    borderRadius: 24, 
    padding: 14, 
    minHeight: 150, 
    justifyContent: 'space-between', 
    borderWidth: 1, 
    borderColor: '#f1f5f9', 
    elevation: 3, 
    boxShadow: '0px 4px 10px rgba(0,0,0,0.05)' 
  },
  cardIconWrap: { width: 44, height: 44, borderRadius: 12, backgroundColor: '#f1f5f9', alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
  cardTitle: { fontSize: 16, fontWeight: 'bold', color: '#0f172a', marginBottom: 4 },
  cardSub: { fontSize: 12, color: '#64748b', lineHeight: 16 },
  aiCard: { backgroundColor: '#e0e7ff', borderColor: '#c7d2fe' },
  aiCardIconWrap: { width: 44, height: 44, borderRadius: 12, backgroundColor: 'white', alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
  aiCardTitle: { fontSize: 16, fontWeight: 'bold', color: '#312e81', marginBottom: 4 },
  aiCardSub: { fontSize: 12, color: '#4338ca', lineHeight: 16 },
  successCard: { backgroundColor: '#dcfce7', borderColor: '#bbf7d0' },
  successCardIconWrap: { width: 44, height: 44, borderRadius: 12, backgroundColor: 'white', alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
  successCardTitle: { fontSize: 16, fontWeight: 'bold', color: '#14532d', marginBottom: 4 },
  successCardSub: { fontSize: 12, color: '#166534', lineHeight: 16 },
  mapWrapper: { flex: 1, marginHorizontal: 16, marginBottom: 24, borderRadius: 28, overflow: 'hidden', borderWidth: 1, borderColor: '#e2e8f0', elevation: 4, backgroundColor: '#f8fafc', boxShadow: '0px 4px 12px rgba(0,0,0,0.05)' },
  modalViewportPlacement: { position: 'absolute' },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.6)', justifyContent: 'flex-end' },
  bottomSheet: { backgroundColor: '#f8fafc', borderTopLeftRadius: 32, borderTopRightRadius: 32, padding: 24, maxHeight: '85%', elevation: 20 },
  sheetHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  sheetSubtitle: { fontSize: 13, color: '#16a34a', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: 4 },
  sheetTitle: { fontSize: 24, fontWeight: 'bold', color: '#0f172a' },
  closeSheetBtn: { padding: 8, backgroundColor: '#e2e8f0', borderRadius: 20 },
  reportBox: { backgroundColor: '#ffffff', padding: 20, borderRadius: 20, marginBottom: 24, borderWidth: 1, borderColor: '#e2e8f0', elevation: 2 },
  reportTitle: { fontSize: 16, fontWeight: 'bold', color: '#0f172a' },
  reportText: { fontSize: 15, color: '#475569', lineHeight: 24 },
  recalcBtn: { backgroundColor: '#e11d48', paddingVertical: 14, paddingHorizontal: 16, borderRadius: 12, alignItems: 'center', marginTop: 20, width: '100%' },
  recalcBtnText: { color: 'white', fontWeight: 'bold', fontSize: 15, textAlign: 'center' },
  emptyAuditText: { fontSize: 14, color: '#64748b', textAlign: 'center', lineHeight: 20, marginBottom: 10 },

  // STILI ANTI-SFASAMENTO PER IL BANNER DI TRADUZIONE DELL'AUDIT TERROIR
  translateDataBannerMini: { backgroundColor: '#f5f3ff', padding: 12, borderRadius: 12, marginBottom: 16, borderWidth: 1, borderColor: '#ddd6fe', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, width: '100%' },
  translateDescMini: { fontSize: 13, color: '#6d28d9', flex: 1, minWidth: 180, lineHeight: 18 },
  translateBtnMini: { backgroundColor: '#6d28d9', paddingVertical: 8, paddingHorizontal: 12, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  translateBtnTextMini: { color: 'white', fontWeight: 'bold', fontSize: 12, textAlign: 'center' },

  wineListTitle: { fontSize: 16, fontWeight: 'bold', color: '#0f172a', marginBottom: 12 },
  wineListItem: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'white', padding: 12, borderRadius: 16, marginBottom: 10, elevation: 1 },
  wineThumb: { width: 50, height: 50, borderRadius: 10, marginRight: 12, backgroundColor: '#f1f5f9' },
  wineThumbPlaceholder: { width: 50, height: 50, borderRadius: 10, marginRight: 12, backgroundColor: '#f1f5f9', alignItems: 'center', justifyContent: 'center' },
  wineItemName: { fontSize: 15, fontWeight: 'bold', color: '#0f172a' },
  wineItemType: { fontSize: 13, color: '#64748b' },
  wineItemQty: { backgroundColor: '#f1f5f9', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 10 },
  wineItemQtyText: { fontWeight: 'bold', color: '#0f172a' },
  systemModalOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.7)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  systemModalBox: { backgroundColor: 'white', borderRadius: 24, padding: 24, width: '100%', maxWidth: 400, elevation: 10 },
  systemModalTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginLeft: 12 },
  systemModalMessage: { fontSize: 16, color: '#475569', lineHeight: 24, marginBottom: 24 },
  systemModalConfirmBtn: { backgroundColor: '#0f172a', paddingVertical: 14, borderRadius: 12, alignItems: 'center' },
  systemModalConfirmText: { color: 'white', fontWeight: 'bold', fontSize: 16 }
});